// Dashboard page with real Supabase data + recent issues
import { useNavigate } from 'react-router-dom'
import { FileText, CheckCircle, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/layout/LoadingSpinner'
import { useIssues } from '@/hooks/useIssues'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  description?: string
}

function StatCard({ title, value, icon, description }: StatCardProps): JSX.Element {
  return (
    <Card className="border-kaycoo-teal-light bg-kaycoo-white">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-kaycoo-charcoal">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-kaycoo-teal-dark">{value}</div>
        {description && (
          <p className="text-xs text-kaycoo-charcoal-2">{description}</p>
        )}
      </CardContent>
    </Card>
  )
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-kaycoo-blue-pale text-kaycoo-blue-dark',
  in_progress: 'bg-amber-50 text-amber-800',
  waiting_info: 'bg-purple-50 text-purple-800',
  resolved: 'bg-kaycoo-teal-pale text-kaycoo-teal-dark',
  closed: 'bg-kaycoo-white-2 text-kaycoo-charcoal-2',
}

export function Dashboard(): JSX.Element {
  const navigate = useNavigate()
  const { issues, stats, isLoading, isStatsLoading } = useIssues({ pageSize: 5 })

  if (isLoading && isStatsLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <LoadingSpinner message="Loading dashboard..." />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-kaycoo-teal-dark">Dashboard</h1>
        <p className="text-kaycoo-charcoal-2">
          Welcome to the D365 F&O AI Issue Assistant
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Issues"
          value={isStatsLoading ? '...' : stats.total}
          icon={<FileText className="h-4 w-4 text-kaycoo-charcoal-2" />}
          description="All time"
        />
        <StatCard
          title="Open Issues"
          value={isStatsLoading ? '...' : stats.open}
          icon={<Clock className="h-4 w-4 text-kaycoo-burgundy" />}
          description="Awaiting resolution"
        />
        <StatCard
          title="In Progress"
          value={isStatsLoading ? '...' : stats.in_progress}
          icon={<Clock className="h-4 w-4 text-amber-600" />}
          description="Being worked on"
        />
        <StatCard
          title="Resolved Issues"
          value={isStatsLoading ? '...' : stats.resolved}
          icon={<CheckCircle className="h-4 w-4 text-kaycoo-teal" />}
          description="Successfully closed"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-kaycoo-teal-light bg-kaycoo-white">
          <CardHeader>
            <CardTitle className="text-kaycoo-teal-dark">Recent Issues</CardTitle>
          </CardHeader>
          <CardContent>
            {issues.length === 0 ? (
              <p className="text-sm text-kaycoo-charcoal-2">No issues yet.</p>
            ) : (
              <div className="space-y-2">
                {issues.map((issue) => (
                  <div
                    key={issue.id}
                    className="flex items-center justify-between rounded-md border border-kaycoo-white-3 p-2.5 cursor-pointer hover:bg-kaycoo-teal-pale/30 transition-colors"
                    onClick={() => navigate(`/issues/${issue.id}`)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-kaycoo-charcoal truncate">{issue.title}</p>
                      <p className="text-xs text-kaycoo-charcoal-2">
                        {issue.d365_version} &middot; {new Date(issue.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="outline" className={`ml-2 text-xs capitalize ${STATUS_COLORS[issue.status] ?? ''}`}>
                      {issue.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-kaycoo-teal-light bg-kaycoo-white">
          <CardHeader>
            <CardTitle className="text-kaycoo-teal-dark">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              onClick={() => navigate('/issues/new')}
              className="w-full rounded-md border border-kaycoo-teal-light p-3 text-left hover:bg-kaycoo-teal-pale/30 transition-colors"
            >
              <p className="text-sm font-medium text-kaycoo-teal-dark">Record New Issue</p>
              <p className="text-xs text-kaycoo-charcoal-2">Start an AI-guided issue recording session</p>
            </button>
            <button
              onClick={() => navigate('/knowledge')}
              className="w-full rounded-md border border-kaycoo-teal-light p-3 text-left hover:bg-kaycoo-teal-pale/30 transition-colors"
            >
              <p className="text-sm font-medium text-kaycoo-teal-dark">Search Knowledge Base</p>
              <p className="text-xs text-kaycoo-charcoal-2">Find solutions from historical resolutions</p>
            </button>
            <button
              onClick={() => navigate('/issues')}
              className="w-full rounded-md border border-kaycoo-teal-light p-3 text-left hover:bg-kaycoo-teal-pale/30 transition-colors"
            >
              <p className="text-sm font-medium text-kaycoo-teal-dark">View All Issues</p>
              <p className="text-xs text-kaycoo-charcoal-2">Browse and manage reported issues</p>
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
